import { useState } from 'react'
import { UserIcon } from '@heroicons/react/24/outline'
import { Order } from '../types/order'
import { apiTime } from '../lib/format'
import HandleExternallyModal from './HandleExternallyModal'

interface IncomingOrderRowProps {
  order: Order
  onCompleted: () => void
}

const formatEuro = (cents: number): string =>
  (cents / 100).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' })

const formatAge = (createdAt: string): string => {
  const created = apiTime(createdAt)
  if (created === 0) return ''
  const minutes = Math.max(0, Math.floor((Date.now() - created) / 60_000))
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  return `${hours}h ago`
}

const IncomingOrderRow: React.FC<IncomingOrderRowProps> = ({ order, onCompleted }) => {
  const [modalOpen, setModalOpen] = useState(false)

  return (
    <li className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 text-xs text-gray-500">
            <span>{order.order_number}</span>
            <span>·</span>
            <span>{formatAge(order.created_at)}</span>
            <span>·</span>
            <span className="font-medium text-gray-700">{formatEuro(order.total_cents)}</span>
          </div>
          <div className="mt-1 flex items-center gap-1.5">
            <UserIcon className="h-4 w-4 text-gray-400 shrink-0" />
            <span className="text-base font-semibold text-gray-900 truncate">
              {order.customer_name || 'No customer name'}
            </span>
          </div>
          <p className="mt-0.5 text-xs text-gray-500">
            Look for this name in OrderLine.
          </p>
        </div>

        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="shrink-0 px-3 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          Handle in OrderLine
        </button>
      </div>

      <HandleExternallyModal
        isOpen={modalOpen}
        orderId={order.id}
        orderNumber={order.order_number}
        customerName={order.customer_name}
        onClose={() => setModalOpen(false)}
        onCompleted={onCompleted}
      />
    </li>
  )
}

export default IncomingOrderRow
